<script lang="ts" setup>
import {ref, onMounted, onBeforeUnmount, onActivated, onDeactivated, computed, onBeforeMount, watch, nextTick} from 'vue';
import {LibraryContentTypeDTO, type ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {EXPLORE_LIBRARY_PAGE_SIZE} from '@/ts/api/modules/content-api';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {usePagedCollection} from '@/ts/composables/stateful/use-paged-collection';
import LibraryCollection from '@/vue/organisms/recco/library-collection.vue';
import {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import {useRoute, useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {pagesPath} from '@/ts/router/pages-path';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {wasPopNavigation} from '@/ts/utils/history-navigation';

// Named explicitly, like ExplorePage: <keep-alive :include> matches on the component name, and
// relying on filename inference for that would be a silent break if the file were ever renamed.
defineOptions({name: 'TopicPage'});

const logger = useLogger('TopicPage');
const api = useApi();
const route = useRoute();
const router = useRouter();
const {t: $t} = useI18n();
const {canAccessExplore} = useFeatureAccess();

/**
 * The Challenges collection reuses this page, addressed as a topic even though it is not one: the
 * grid, the paging, the keep-alive refresh and the back label are the same, and only the facet that
 * selects the rows differs (IPD-3209).
 */
const CHALLENGES_COLLECTION = 'challenges';
type TopicRouteParam = ReccoTopicDTO | typeof CHALLENGES_COLLECTION;

const topic = computed<TopicRouteParam>(() => route.params.topic as TopicRouteParam);
const isChallenges = computed<boolean>(() => topic.value === CHALLENGES_COLLECTION);

/** The collection the grid currently holds rows for. Null until the first fetch starts. */
const loadedTopic = ref<TopicRouteParam | null>(null);
// Same window explore-page uses for its own silent refresh.
const REFRESH_STALE_AFTER_MS = 5 * 60 * 1000;
let lastLoadedAt = 0;

// Explore never surfaces programs, so they are excluded server-side: requesting them would count
// them into totalElements and the header would promise rows the grid never renders.
const TOPIC_FACETS = {
  mediaTypes: [],
  saved: false,
  recommended: false,
  partnerIds: [],
  contentTypes: [LibraryContentTypeDTO.Challenge, LibraryContentTypeDTO.Educational],
};

const collectionState = usePagedCollection<ExploreLibraryItemDTO>({
  key: (item) => `${item.contentType}-${item.content?.id ?? item.challenge?.id}`,
  rootSelector: '.global-page-template .content',
  onMissingRoot: () => {
    logger.error('Topic grid found no .content scroller; paging falls back to the viewport root')();
  },
  fetchPage: async(page) => {
    // Challenges are selected by content type across every topic; a topic selects both content
    // types within one topic.
    const facets = isChallenges.value
      ? {...TOPIC_FACETS, contentTypes: [LibraryContentTypeDTO.Challenge], topics: []}
      : {...TOPIC_FACETS, topics: [topic.value as ReccoTopicDTO]};
    const result = await api.content.getExploreLibrary(
      facets,
      EXPLORE_LIBRARY_PAGE_SIZE,
      page,
    );
    return {items: result.items, page: result.page, totalElements: result.totalElements};
  },
  onError: (err, first) => {
    logger.error(first ? 'Failed to fetch the topic library' : 'Failed to load more of the topic library', {err})();
  },
});

const {items, totalElements, loadedCount, loading, loadingMore, error, moreError, stoppedShort,
  collection, setSentinel, setFirstPageLoader, mountObserver, syncObserver, pauseObserver, disconnectObserver,
  loadFirstPage, refreshInPlace, retryFirstPage, retryMorePages} = collectionState;

const fetchFirstPage = async(): Promise<void> => {
  // Set synchronously, before the first await: it is what the grid is showing (or is on its way to
  // showing), and both the watcher and onActivated compare against it to decide whether a refetch
  // is owed. Lifecycle state cannot answer that — see the watcher below.
  loadedTopic.value = topic.value;
  await loadFirstPage();
  if (error.value) {
    // Neither claim is true after a failure: the grid holds no rows for this topic, and stamping
    // the clock would buy the staleness gate five quiet minutes on the strength of a failed load.
    loadedTopic.value = null;
  } else {
    lastLoadedAt = Date.now();
  }
};

// Retry goes through the page's own load: the list-only one leaves loadedTopic null, and every
// later return to the topic would then see a change and reload from scratch, throwing away the
// pages the reader had scrolled — the exact thing keep-alive is on this page to prevent.
setFirstPageLoader(fetchFirstPage);

const topicTitleKey = computed<string>(() => {
  if (isChallenges.value) {
    return 'recco_challenges';
  }
  switch (topic.value) {
    case ReccoTopicDTO.PHYSICAL_ACTIVITY:
      return 'recco_physical_activity';
    case ReccoTopicDTO.NUTRITION:
      return 'recco_nutrition';
    case ReccoTopicDTO.MENTAL_WELLBEING:
      return 'recco_mental_wellbeing';
    case ReccoTopicDTO.RELAXATION:
      return 'recco_relaxation';
    case ReccoTopicDTO.SLEEP:
      return 'recco_sleep';
    default:
      return '';
  }
});

const topicTitle = computed<string>(() => (topicTitleKey.value ? $t(topicTitleKey.value) : ''));
const countLabel = computed<string>(() =>
  $t('explore_n_items', totalElements.value, {named: {count: totalElements.value}}));
const loadedStatusLabel = computed<string>(() =>
  $t('explore_topic_loaded_status', totalElements.value, {
    named: {loaded: loadedCount.value, count: totalElements.value},
  }));

onBeforeMount(() => {
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
  }
});

let unmounted = false;

onMounted(async() => {
  await fetchFirstPage();
  // The component can be gone by the time the first page lands (fast back-navigation, or the
  // access redirect above), and the observer below would then outlive onBeforeUnmount.
  if (unmounted) {
    return;
  }
  mountObserver();
  await nextTick();
  syncObserver();
});

onBeforeUnmount(() => {
  unmounted = true;
  disconnectObserver();
});

onDeactivated(() => {
  pauseObserver();
});

onActivated(() => {
  // Re-checked on every visit: a licence can drop the feature mid-session, and a cached instance
  // never re-runs the onBeforeMount guard.
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
    return;
  }
  // Re-entered on a different topic: the watcher below has usually already started that fetch, in
  // which case loadedTopic matches and this is a no-op. This is the backstop for any path that
  // reaches here without it having fired.
  if (topic.value && loadedTopic.value !== topic.value) {
    void fetchFirstPage();
    return;
  }
  // Same topic. Refreshed in place rather than restarting the walk, which would drop every page
  // the reader had scrolled — the thing keep-alive is here to prevent (IPD-3035).
  if (!wasPopNavigation() || Date.now() - lastLoadedAt > REFRESH_STALE_AFTER_MS) {
    void refreshInPlace().then((refreshed) => {
      if (refreshed) {
        lastLoadedAt = Date.now();
      }
    });
    return;
  }
  void nextTick(() => {
    syncObserver();
  });
});

// Navigating between topics without remounting must restart from the first page.
watch(topic, (next) => {
  // Keyed on what is loaded, not on whether the page is active. An earlier version guarded on an
  // `active` flag set in onActivated, which cannot work: this watcher is pre-flush and onActivated
  // is a post-render effect, so on re-entry the watcher runs while the flag is still false, skips,
  // and the flag flips with no topic change left to react to.
  //
  // `!next` is the leaving case: route.params.topic goes undefined on the way out, and this page
  // stays alive in the keep-alive cache to receive it.
  if (!next || next === loadedTopic.value) {
    return;
  }
  void fetchFirstPage();
});
</script>

<template>
  <library-collection
    ref="collection"
    :title="topicTitle"
    :count-label="countLabel"
    :status-label="loadedStatusLabel"
    :back-label="topicTitleKey"
    :items="items"
    :loading="loading"
    :error="error"
    :loading-more="loadingMore"
    :show-retry-more="moreError || stoppedShort"
    :set-sentinel="setSentinel"
    @retry-first="retryFirstPage"
    @retry-more="retryMorePages"
  />
</template>
